/**
 * A live dataset's source: the trace connection it pulls from, the filters as
 * chips and how many traces each run keeps. Edit changes the next pull only;
 * versions already pulled stay as they are.
 */

import { useState } from "react";
import { Box, BoxHeader } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { QueryState } from "@/components/ui/states";
import { useProject } from "@/features/projects/hooks";
import { qflowChips } from "@/features/traces/qflow-query";
import { useWorkspaceNames } from "@/features/traces/qflow-workspace";
import { useSourceKind } from "@/features/traces/sources";
import { fromFilters } from "@/features/traces/trace-query";
import { formatCount } from "@/lib/format";
import { useDatasetSource } from "../hooks";
import type { SourceFilters } from "../types";
import { SourceEditForm } from "./source-edit";

/** One readable chip per filter that narrows the pull. */
export function filterChips(f: SourceFilters): string[] {
  const list = (label: string, values: string[] | undefined) =>
    values?.length ? [`${label}: ${values.join(", ")}`] : [];
  return [
    ...list("Trace name", f.trace_name),
    ...list("Environment", f.environment),
    ...list("Type", f.type),
    ...list("Name", f.name),
    ...(f.is_root === true ? ["Root only"] : f.is_root === false ? ["Not root"] : []),
    ...(f.version ? [`Version: ${f.version}`] : []),
    ...Object.entries(f.columns ?? {}).flatMap(([column, values]) => list(column, values)),
    ...(f.skip_failed !== false ? ["Skips failed"] : []),
  ];
}

/** `readOnly` for an archived dataset: no editing until it is restored. */
export function SourceBox({ datasetId, readOnly = false }: { datasetId: string; readOnly?: boolean }) {
  const source = useDatasetSource(datasetId);
  const [editing, setEditing] = useState(false);
  const mayEdit = useProject().can("datasets:write") && !readOnly;
  const s = source.data;
  const { connection, qflow } = useSourceKind(s?.connection_id);
  const workspaces = useWorkspaceNames(qflow ? (s?.connection_id ?? "") : "");
  const chips = !s ? [] : qflow ? qflowChips(fromFilters(s.filters), workspaces) : filterChips(s.filters);

  return (
    <Box label="Source">
      <BoxHeader title="Source">
        <span className="flex-1 text-xs text-muted">
          Each experiment run pulls a new version from the source.
        </span>
        {s && !editing && mayEdit && (
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </BoxHeader>
      <QueryState query={source} what="the source">
        {s &&
          (editing ? (
            <SourceEditForm datasetId={datasetId} source={s} onDone={() => setEditing(false)} />
          ) : (
            <div className="flex flex-col gap-2.5 px-4 py-3 text-[13px]">
              <span>
                {qflow ? "qFlow runs from" : "Traces from"}{" "}
                <span className="font-semibold">{connection?.name ?? "a deleted connection"}</span>
              </span>
              <ul aria-label="Source filters" className="flex flex-wrap gap-1.5">
                {chips.length ? (
                  chips.map((c) => (
                    <li key={c}>
                      <Pill>{c}</Pill>
                    </li>
                  ))
                ) : (
                  <li className="text-muted">Every trace</li>
                )}
              </ul>
              <span className="text-muted">
                Keeps up to {formatCount(s.limit)} {qflow ? "runs" : "traces"} per run.
              </span>
            </div>
          ))}
      </QueryState>
    </Box>
  );
}
